import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { getDefaultDisplayDate } from 'src/app/common/utils';
import { CampaignSelectedSalesMaterialItem } from 'src/app/modules/PulseLeads/types/campaign-types';

export type CampaignMaterialSelectionListItem = {
  uuid: string;
  name: string;
  section: string;
  sectionKey: string;
  category: string;
  status: string;
  type: string;
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
  lastUpdatedBy?: string;
  effectiveDate?: Date | string | null;
  expiredDate?: Date | string | null;
  publishedAt?: Date | string | null;
  disableSelection?: boolean;
};

export type CampaignMaterialSelectionPaginateList = {
  libraryList: CampaignMaterialSelectionListItem[];
  totalPages: number;
  totalRecords: number;
};

export type CampaignMaterialSelectionSelectedRow =
  | CampaignMaterialSelectionListItem
  | CampaignSelectedSalesMaterialItem;

type EditMaterialSetListTableProps = {
  isLoading: boolean;
  libraryList?: CampaignMaterialSelectionPaginateList;
  enableCreate: boolean;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onSort: (sortingState: { key: string; value?: string }) => void;
  setCurrentSelectedRow: (data: CampaignMaterialSelectionSelectedRow[]) => void;
  updateSelectedRow?: CampaignMaterialSelectionSelectedRow[];
  defaultPageNumber?: number;
  defaultRowsPerPage?: number;
};

const getTypeLabel = (type?: string) => {
  if (!type) {
    return '';
  }

  const typeLabelMap: Record<string, string> = {
    file: 'File',
    materialSet: 'Material Set',
    content: 'Content',
    link: 'Link',
    text: 'Text',
    picture: 'Picture',
  };

  return typeLabelMap[type] || type;
};

const getStatusMessageId = (status?: string) => {
  if (!status) {
    return '';
  }

  return `component.status.${status.toLowerCase()}`;
};

const getDisplayDate = (date?: Date | string | null) => {
  if (!date || date === 'N/A') {
    return 'N/A';
  }

  const parsedDate = new Date(date);

  return Number.isNaN(parsedDate.getTime()) ? 'N/A' : getDefaultDisplayDate(parsedDate, 'datetime');
};

const EditMaterialSetListTable: FC<EditMaterialSetListTableProps> = ({
  isLoading,
  libraryList,
  enableCreate,
  onRefresh,
  onChangePage,
  onSort,
  setCurrentSelectedRow,
  updateSelectedRow,
  defaultPageNumber,
  defaultRowsPerPage,
}) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  return (
    <PruTable
      headerBtnDef={[]}
      operationDef={[]}
      columnDef={[
        {
          isId: true,
          keyIndex: 'uuid',
          hidden: true,
          displayName: '',
          renderData: () => '',
        },
        {
          keyIndex: 'name',
          align: 'left',
          displayName: Translation('component.formLabel.name'),
          sortable: true,
          renderData: (row) => String(row.name ? row.name : ''),
          onSort: (sort) => {
            onSort({ key: 'name', value: sort['name'] });
          },
        },
        {
          keyIndex: 'section',
          align: 'left',
          displayName: Translation('pulseleads.campaign.salesMaterial.section'),
          sortable: true,
          renderData: (row) => String(row.section ? row.section : ''),
          onSort: (sort) => {
            onSort({ key: 'section', value: sort['section'] });
          },
        },
        {
          keyIndex: 'category',
          align: 'left',
          displayName: Translation('component.formLabel.category'),
          sortable: true,
          renderData: (row) => String(row.category ? row.category : ''),
          onSort: (sort) => {
            onSort({ key: 'category', value: sort['category'] });
          },
        },
        {
          keyIndex: 'status',
          align: 'left',
          displayName: Translation('component.formLabel.status'),
          sortable: true,
          renderData: (row) => {
            const messageId = getStatusMessageId(row.status);

            return messageId ? Translation(messageId) : String(row.status || '');
          },
          onSort: (sort) => {
            onSort({ key: 'status', value: sort['status'] });
          },
        },
        {
          keyIndex: 'type',
          align: 'left',
          displayName: Translation('component.formLabel.type'),
          renderData: (row) => getTypeLabel(row.type),
          sortable: true,
          onSort: (sort) => {
            onSort({ key: 'type', value: sort['type'] });
          },
        },
        {
          keyIndex: 'createdAt',
          align: 'left',
          displayName: Translation('component.formLabel.created-time'),
          renderData: (row) => getDisplayDate(row.createdAt),
          sortable: true,
          onSort: (sort) => {
            onSort({ key: 'createdAt', value: sort['createdAt'] });
          },
        },
        {
          keyIndex: 'updatedAt',
          align: 'left',
          displayName: Translation('component.formLabel.last-updated-time'),
          renderData: (row) => getDisplayDate(row.updatedAt),
          sortable: true,
          onSort: (sort) => {
            onSort({ key: 'updatedAt', value: sort['updatedAt'] });
          },
        },
        {
          keyIndex: 'lastUpdatedBy',
          align: 'left',
          displayName: Translation('component.formLabel.last-updated-by'),
          renderData: (row) => String(row.lastUpdatedBy ? row.lastUpdatedBy : ''),
          sortable: true,
          onSort: (sort) => {
            onSort({ key: 'lastUpdatedBy', value: sort['lastUpdatedBy'] });
          },
        },
        {
          keyIndex: 'effectiveDate',
          align: 'left',
          displayName: Translation('component.formLabel.effective-date-range-start'),
          sortable: true,
          renderData: (row) => getDisplayDate(row.effectiveDate),
          onSort: (sort) => {
            onSort({ key: 'effectiveDate', value: sort['effectiveDate'] });
          },
        },
        {
          keyIndex: 'expiredDate',
          align: 'left',
          displayName: Translation('component.formLabel.effective-date-range-end'),
          sortable: true,
          renderData: (row) => getDisplayDate(row.expiredDate),
          onSort: (sort) => {
            onSort({ key: 'expiredDate', value: sort['expiredDate'] });
          },
        },
      ]}
      defaultPageNumber={defaultPageNumber}
      defaultRowsPerPage={defaultRowsPerPage || 10}
      isLoading={isLoading}
      onRefresh={onRefresh}
      dataSource={libraryList?.libraryList}
      totalPages={libraryList?.totalPages}
      totalRecords={libraryList?.totalRecords}
      onChangePage={onChangePage}
      currentSelectedRow={setCurrentSelectedRow}
      updateSelectedRow={updateSelectedRow}
      disableBulkSelect={!enableCreate}
      bulkSelectCheckboxDisable={(rowData) => {
        return rowData.disableSelection;
      }}
    />
  );
};

export default EditMaterialSetListTable;
